import React, { useState, useEffect } from 'react';
import style from './message.module.scss';
import { Button } from 'antd';
import {
	NextNode,
	TabSelector,
	OnEnter,
	OnReceive,
	AddMessagePanel,
} from './components';
import { MessageSchema, AttachedMsg } from './types';
import { useAppAndBranchId, useQuery } from '../../../chat/hooks';
import { api } from '../../../chat/pages/content/api';
import { uuid } from '../../../../../../helpers/commonFunction';

function Message(props: any) {
	const { elements, nodeDetail } = props;
	const [tab, setTab] = useState<'onEnter' | 'onReceive'>('onEnter');
	const [isOpen, setIsOpen] = useState(false); // "add action" visibility
	const [appId, branchId] = useAppAndBranchId();
	const [error, setError] = useState({
		name: '',
		common: '',
	});

	const hasError = !Object.values(error).every((el) => el === '');

	const existingNames: string[] = elements
		.filter((el: any) => el.id !== nodeDetail.id)
		.map((el: any) => el.data?.propertyDetails?.name)
		.filter((el: any) => el);

	const [message, setMessage] = useState<MessageSchema>({
		name: '',
		nextNode: '',
		onEnter: [],
		waitForUserMessage: false,
		onReceive: [],
	});

	const [editAction, setEditAction] = useState({
		index: -1,
		contentId: '',
		contentType: '',
		isDynamic: false,
		title: '',
		source: '',
	});

	const contentIds = message.onEnter
		.concat(message.onReceive ?? [])
		.filter((el) => !el.isDynamic)
		.map((el: any) => el.contentId);

	const { data: contents, loading } = useQuery(
		() => api.getChatContentsByIds(appId, branchId, contentIds),
		contentIds.join(","),
		contentIds.length > 0,
	);

	function toggleShouldWait() {
		setError((prev) => ({ ...prev, common: '' }));
		updateStateByKey('waitForUserMessage', !message.waitForUserMessage);
		if(!message.waitForUserMessage === false) {
			updateStateByKey('onReceive', []);
		}
	}

	function updateStateByKey(key: keyof MessageSchema, value: any) {
		setMessage((prev) => ({
			...prev,
			[key]: value,
		}));
	}

	function onAdd(data: AttachedMsg) {
		setError((err) => ({ ...err, common: '' }));
		updateStateByKey(tab, [...message[tab], data]);
	}

	function onEdit(index: number, data: AttachedMsg) {
		updateStateByKey(
			tab,
			message[tab].map((el, i) => (i === index ? data : el)),
		);
	}

	function removeAction(index: number) {
		updateStateByKey(
			tab,
			message[tab].filter((_, i) => i !== index),
		);
	}

	function updateProperties() {
		props.onUpdateProperties({
			nodeId: props.nodeDetail.id,
			idChange: true,
			...message,
		});
		props.setWidgetDrawer(false);
		setMessage({
			name: '',
			nextNode: '',
			onReceive: [],
			onEnter: [],
			waitForUserMessage: false,
		});
	}

	function validateMessageNode() {
		const error: Record<string, string> = {};
		if (!message.name.trim().length) {
			error['name'] = "Name shouldn't be empty";
		}

		if (existingNames.includes(message.name.trim())) {
			error['name'] = 'Name already exists';
		}

		if (
			!message.waitForUserMessage &&
			!(message.onEnter.length || message.onReceive.length)
		) {
			error['common'] =
				"Please provide a message in 'OnEnter' or 'OnReceive', or enable 'Wait for User Message'.";
		}

		const errorLen = Object.keys(error).length;
		if (errorLen) {
			setError(error as any);
		}
		return errorLen === 0;
	}

	function setList(data: any[]) {
		setMessage((prev) => ({
			...prev,
			[tab]: data,
		}));
	}

	useEffect(() => {
		if (props.widgetDrawer) {
			setError({ common: '', name: '' });
			setTab('onEnter');
			const propertyDetails = nodeDetail.data.propertyDetails ?? {};
			const nextNode = props.elements.find((el: any) => {
				return el.source === nodeDetail.id;
			});
			setMessage({
				name: propertyDetails.name ?? props.nodeDetail.id,
				// _id is used internally for reordering
				onEnter: (propertyDetails.onEnter ?? []).map((el: any) => ({
					...el,
					_id: uuid(),
				})),
				onReceive: (propertyDetails.onReceive ?? []).map((el: any) => ({
					...el,
					_id: uuid(),
				})),
				nextNode: nextNode?.target ?? propertyDetails.nextNode ?? '',
				waitForUserMessage: propertyDetails.waitForUserMessage ?? false,
			});
		}
	}, [props.widgetDrawer, nodeDetail.data.propertyDetails]);

	// update next node when changes happen in the canvas
	useEffect(() => {
		const findNextNode = elements?.find((el: any) => {
			return el?.source === nodeDetail.id;
		});
		const findTargetNode = elements?.find((el: any) => {
			return el?.target === message.nextNode;
		});
		if (findNextNode) {
			updateStateByKey('nextNode', findNextNode.target);
		} else if (!findNextNode && findTargetNode) {
			updateStateByKey('nextNode', '');
		}
	}, [elements]);

	return (
		<div className={style.message}>
			<div className={style.inputWrapper}>
				<label htmlFor="messageNodeName">Name</label>
				<input
					disabled={props.lock}
					value={message.name}
					onChange={(e) => {
						const value = e.currentTarget.value;
						setError((err) => ({
							...err,
							name: value.trim() === '' ? "Name shouldn't be empty" : '',
						}));
						if (existingNames.includes(value.trim())) {
							setError((err) => ({ ...err, name: 'Name already exists' }));
						}
						updateStateByKey('name', value);
					}}
					id="messageNodeName"
					placeholder="Enter name here"
				/>
				{error.name && (
					<span className={style.error} style={{ textAlign: 'center' }}>
						{error.name}
					</span>
				)}
			</div>
			<NextNode
				onNodeSelect={(node) => updateStateByKey('nextNode', node)}
				nextNode={message.nextNode}
				lock={props.lock}
				elements={props.elements}
				nodeDetail={props.nodeDetail}
			/>
			<hr />

			<div className={style.unset}>
				<TabSelector selected={tab} onTabSelect={(tab) => setTab(tab)} />

				{message[tab].length > 0 && (
					<button
						disabled={props.lock}
						className={style.addMessage}
						onClick={() => setIsOpen(true)}
					>
						Add Message
					</button>
				)}

				{!loading && tab === 'onEnter' && (
					<OnEnter
						setList={setList}
						lock={props.lock}
						removeAction={removeAction}
						contents={contents ?? []}
						message={message}
						viewPanel={(visibility) => setIsOpen(visibility)}
						setEditAction={setEditAction}
					/>
				)}
				{tab === 'onReceive' && (
					<OnReceive
						removeAction={removeAction}
						setList={setList}
						lock={props.lock}
						contents={contents ?? []}
						toggleShouldWait={toggleShouldWait}
						message={message}
						viewPanel={(visibility) => setIsOpen(visibility)}
						setEditAction={setEditAction}
					/>
				)}
				{error.common && (
					<span style={{ marginTop: 8 }} className={style.error}>
						{error.common}
					</span>
				)}
				<AddMessagePanel
					isOpen={isOpen}
					onAdd={onAdd}
					close={() => setIsOpen(false)}
					attachedMsgs={message[tab]}
				/>
				<AddMessagePanel
					attachedMsgs={message[tab]}
					isOpen={editAction.index >= 0}
					initialValue={editAction}
					onEdit={onEdit}
					isEdit
					close={() =>
						setEditAction({
							contentType: '',
							contentId: '',
							index: -1,
							title: '',
							source: '',
							isDynamic: false,
						})
					}
				/>
			</div>

			<div className={style.footer}>
				{!props.lock && (
					<Button
						disabled={hasError}
						style={{
							backgroundColor: '#F1722E',
							color: '#FFFFFF',
							width: '100%',
							height: 50,
						}}
						onClick={() => {
							if (!validateMessageNode()) {
								return;
							}
							updateProperties();
						}}
					>
						APPLY
					</Button>
				)}
			</div>
		</div>
	);
}

export default Message;
